import React from 'react';

import { IconButton, Text } from '@fly/core';
import { type DialogWidth } from '@fly/store';
import { Dialog } from '@radix-ui/themes';
import cn from 'classnames';

interface GlobalDialogProps {
  isOpen: boolean;
  title: string | React.ReactNode;
  description?: React.ReactNode;
  content: React.ReactNode | null;
  width?: DialogWidth;
  showCloseDialog?: boolean;
  close?: () => void;
  /**
   * Stable selector for E2E tests. Set when a single dialog instance needs to
   * be detected reliably (e.g. the session-lost dialog the test framework
   * recovers from). When set, lands on the Radix ``Dialog.Content`` element.
   */
  testId?: string;
}

const dialogWidthClassNames: Record<DialogWidth, string> = {
  '1': 'w-[268px]',
  '2': 'w-[400px]',
  '3': 'w-[400px]',
  '4': 'w-[450px]',
  '5': 'w-[500px]',
};

export const FlyGlobalDialog: React.FC<GlobalDialogProps> = ({
  isOpen,
  title,
  description,
  content,
  width = '2',
  showCloseDialog = false,
  close,
  testId,
  ...rest
}) => {
  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Content
        size='2'
        className={cn('p-fly-5', dialogWidthClassNames[width])}
        data-testid={testId}
        {...rest}
      >
        {showCloseDialog && close && (
          <IconButton
            data-testid='fly-global-dialog-close-btn'
            variant='ghost'
            color='neutral'
            icon='cross'
            className='right-3 top-3 absolute'
            onClick={close}
          />
        )}
        {title && (
          <Dialog.Title
            className={cn(
              description ? 'mb-fly-2' : 'mb-fly-6',
              showCloseDialog && 'mr-[32px]'
            )}
          >
            {typeof title === 'string' ? (
              <Text size='3' color='neutral' highContrast>
                {title}
              </Text>
            ) : (
              title
            )}
          </Dialog.Title>
        )}
        {description && (
          <Dialog.Description size='1'>{description}</Dialog.Description>
        )}
        {content}
      </Dialog.Content>
    </Dialog.Root>
  );
};
